import { safeFetch, getBaseUrl, authFetch } from './api';

// The clinic's logo and letterhead banner, and a doctor's signature (routes: /branding)
export type BrandingKind = 'logo' | 'letterhead' | 'signature';

export interface BrandingImages {
  logo: string | null;
  letterhead: string | null;
  signature: string | null;
}

// Fit inside these sizes before upload: sharp when printed, small to store. Byte budgets match the server's limits.
const LIMITS: Record<BrandingKind, { maxW: number; maxH: number; maxBytes: number }> = {
  logo:       { maxW: 600,  maxH: 600, maxBytes: 500 * 1024 },
  letterhead: { maxW: 2480, maxH: 800, maxBytes: 1500 * 1024 },
  signature:  { maxW: 900,  maxH: 400, maxBytes: 500 * 1024 },
};

/** Data URLs, null where none is set; the signature is the given doctor's */
export async function getBrandingImages(doctorId?: string | null): Promise<BrandingImages | null> {
  const q = doctorId ? `?doctor_id=${encodeURIComponent(doctorId)}` : '';
  return safeFetch<BrandingImages>(`${getBaseUrl()}/branding${q}`);
}

/** Address of a clinic's logo for its public sign-in page; version changes whenever the logo does */
export function clinicLogoUrl(slug: string, version: number) {
  return `${getBaseUrl()}/auth/clinic/${encodeURIComponent(slug)}/logo?v=${version}`;
}

/**
 * Shrink a picked image to the kind's size and re-encode it: PNG keeps a logo's transparency, and anything too
 * heavy as PNG (a photographed letterhead) becomes JPEG on white.
 */
async function prepareImage(file: File, kind: BrandingKind): Promise<Blob> {
  const { maxW, maxH, maxBytes } = LIMITS[kind];
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('This image could not be read. Use a PNG or JPG.');
  });
  const scale = Math.min(1, maxW / bitmap.width, maxH / bitmap.height);
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  const encode = (type: string, quality?: number) =>
    new Promise<Blob>((resolve, reject) => canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not process the image'))), type, quality));

  if (file.type !== 'image/jpeg') {
    ctx.drawImage(bitmap, 0, 0, w, h);
    const png = await encode('image/png');
    if (png.size <= maxBytes) return png;
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  for (const quality of [0.9, 0.8, 0.7]) {
    const jpg = await encode('image/jpeg', quality);
    if (jpg.size <= maxBytes) return jpg;
  }
  throw new Error('This image is too detailed to use. Try a simpler or smaller one.');
}

// Upload uses FormData directly (not safeFetch): safeFetch always sends application/json
export async function uploadBrandingImage(kind: BrandingKind, file: File): Promise<void> {
  const blob = await prepareImage(file, kind);
  const formData = new FormData();
  formData.append('file', blob, `${kind}.${blob.type === 'image/png' ? 'png' : 'jpg'}`);
  const res = await authFetch(`${getBaseUrl()}/branding/${kind}`, { method: 'PUT', body: formData });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Upload failed (HTTP ${res.status})`);
  }
}

export async function removeBrandingImage(kind: BrandingKind): Promise<void> {
  await safeFetch(`${getBaseUrl()}/branding/${kind}`, { method: 'DELETE' }, 0);
}
